import { useMemo } from 'react';

import FormWidget from '@/components/form-widget';
import { TemplateColor } from '@/components/template-page-layout/template-page-layout.types';

import { TFormWidget } from '@/shared/services/forms/forms.types';

import { cn } from '@/lib/utils';

import {
	FORM_WIDGET_APPLICATIONS_THRESHOLD,
	FORM_WIDGET_RESPONSE_RATE_THRESHOLD,
} from '@/utils/constants';
import { getAmPmTimeFromUTC, getDayMonthFromUTC, getTimeDifferenceWithPresent } from '@/utils/date';

type FormWidgetData = {
	type: TFormWidget;
	text: string;
	tooltipText?: string;
};

type FormWidgetsDisplayProps = {
	includedWidgets: TFormWidget[];
	closingDate?: string | null;
	totalCount?: number;
	respondedCount?: number;
	formStatus?: string;
	templateColor: TemplateColor;
	formWidgetClassName?: string;
	className?: string;
	locations?: string[];
	wrapText?: boolean;
	variant?: 'default' | 'minimal';
};

const WIDGETS_DESIRED_ORDER: TFormWidget[] = [
	'LOCATION',
	'CLOSING_DATE',
	'NUMBER_OF_APPLICANTS',
	'RESPONSE_PERCENTAGE',
];

function FormWidgetsDisplay({
	includedWidgets,
	closingDate,
	totalCount,
	respondedCount,
	formStatus,
	templateColor,
	formWidgetClassName,
	className,
	locations,
	wrapText,
	variant,
}: FormWidgetsDisplayProps) {
	const widgetData = useMemo<FormWidgetData[]>(() => {
		if (!includedWidgets.length) return [];

		const sortedWidgets = [...includedWidgets].sort(
			(a, b) => WIDGETS_DESIRED_ORDER.indexOf(a) - WIDGETS_DESIRED_ORDER.indexOf(b)
		);

		return sortedWidgets.reduce<FormWidgetData[]>((acc, widget) => {
			let text = '';
			let tooltipText: string | undefined;

			switch (widget) {
				case 'LOCATION': {
					if (!locations?.length) break;

					text = locations.join(', ');
					break;
				}
				case 'CLOSING_DATE': {
					if (!closingDate || formStatus === 'CLOSED') break;

					const timeDifference = getTimeDifferenceWithPresent(closingDate);
					if (timeDifference <= 0) break;

					text = `Apply by ${getDayMonthFromUTC(closingDate)}`;
					tooltipText = `${text}, ${getAmPmTimeFromUTC(closingDate)}`;
					break;
				}
				case 'NUMBER_OF_APPLICANTS': {
					if (totalCount && totalCount >= FORM_WIDGET_APPLICATIONS_THRESHOLD) {
						text = `${totalCount} Candidates`;
					}
					break;
				}
				case 'RESPONSE_PERCENTAGE': {
					if (!respondedCount || !totalCount) break;

					const respondedPercentage = (respondedCount / totalCount) * 100;
					if (respondedPercentage >= FORM_WIDGET_RESPONSE_RATE_THRESHOLD) {
						text = `Responded to ${respondedPercentage.toFixed(0)}% candidates`;
					}
					break;
				}
			}

			if (text) {
				acc.push({ type: widget, text, tooltipText });
			}

			return acc;
		}, []);
	}, [
		includedWidgets,
		closingDate,
		formStatus,
		totalCount,
		respondedCount,
		locations,
	]);

	if (!widgetData.length) return null;

	return (
		<div className={cn('my-4 flex w-full flex-wrap items-center gap-4 md:my-5', className)}>
			{widgetData.map((w) => (
				<FormWidget
					key={w.type}
					widgetType={w.type}
					text={w.text}
					color={templateColor}
					tooltipText={w.tooltipText}
					className={formWidgetClassName}
					variant={variant}
					wrapText={wrapText}
				/>
			))}
		</div>
	);
}

export default FormWidgetsDisplay;
